import { Box, Card, CardContent } from "@mui/material";
import { displayBlockDate, displayPageType, GetLensRunResponse, LensHit, PageType } from "Backend";
import CollapsibleSections, { CollapsibleSection } from "components/CollapsibleSections";
import LoadingSpinner from "components/LoadingSpinner";
import Tag from "components/Tag";
import { DocumentIntake, DocumentSegment, FormField } from "generated/graphql";
import { collectFirst, groupBy, nonEmpty } from "helpers/coll";
import { useMemo } from "react";
import { useAppSelector } from "redux/hooks";
import { findDocumentSegmentInPageRange } from "redux/slices/case";
import { selectDocumentSegmentPageCache } from "redux/slices/intakeViewer";
import { P } from "theme/typography";

interface LensHitSectionProps {
  lensHit: LensHit;
  numHits: number;
  profileFieldId: number | undefined;
}

const LensHitSection = ({ lensHit, numHits, profileFieldId }: LensHitSectionProps) => {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <P variant="body4" sx={{ color: "grey.400" }}>
        {profileFieldId === undefined ? `${lensHit.ruleSubtype} - ` : ""}
        {lensHit.ruleDescription}
        {numHits > 1 ? ` (${numHits})` : ""}
      </P>
      <P variant="body4" sx={{ color: "grey.400", flexShrink: 0 }}>
        {displayBlockDate(lensHit.block.date)}
      </P>
    </Box>
  );
};

interface HitsOnPage {
  intake: DocumentIntake;
  pageNum: number;
  profileFieldId: number | undefined;
  formFields: FormField[];
}

const HitsOnPage = ({ intake, pageNum, profileFieldId, formFields }: HitsOnPage) => {
  const currentSegment: DocumentSegment | undefined = useMemo(
    () => findDocumentSegmentInPageRange(intake, pageNum),
    [intake.id, pageNum]
  );
  const currentField = useMemo(() => formFields.find((f) => f.id === profileFieldId), [profileFieldId, formFields]);
  const segmentPageNum = currentSegment ? pageNum - currentSegment.startPage + 1 : pageNum;
  const pageCache = useAppSelector((state) =>
    selectDocumentSegmentPageCache(state, [currentSegment?.id, segmentPageNum])
  );
  const lensRun: GetLensRunResponse | undefined = pageCache?.lensRun;
  const formFieldIdMap = formFields.reduce((prev, curr, currentIndex) => {
    prev[curr.id] = currentIndex;
    return prev;
  }, {});

  const pageType: PageType | undefined = collectFirst(lensRun?.hits ?? [], (hit) => hit.pageType);
  const pageLabels: string[] = collectFirst(lensRun?.hits ?? [], (hit) => hit.pageLabels) ?? [];

  // c'mon intake viewer, load the hits already!
  if (!lensRun || !lensRun.hits)
    return (
      <Card>
        <CardContent>
          <LoadingSpinner size={20} />
        </CardContent>
      </Card>
    );

  const filteredHits =
    profileFieldId !== undefined ? lensRun.hits.filter((h) => h.formFieldId === profileFieldId) : lensRun.hits;
  const sortedHits = filteredHits
    .slice(0)
    .sort(
      (a, b) =>
        (a.formFieldId !== undefined ? formFieldIdMap[a.formFieldId] : Number.MAX_SAFE_INTEGER) -
        (b.formFieldId !== undefined ? formFieldIdMap[b.formFieldId] : Number.MAX_SAFE_INTEGER)
    );

  const groupings =
    profileFieldId !== undefined
      ? groupBy<LensHit, string>(sortedHits, (hit) => hit.ruleSubtype)
      : groupBy<LensHit, string>(sortedHits, (hit) => {
          const field = formFields.find((f) => f.id === hit.formFieldId);
          return field ? `${field.outlinePrefix} ${field.name}` : "No Field";
        });
  const sections: CollapsibleSection[] = [];

  for (const [groupHeader, lensHits] of groupings) {
    const hitMap = lensHits.reduce((prev, hit) => {
      const hitHash = `${hit.ruleSubtype}${hit.ruleDescription}${displayBlockDate(hit.block.date || undefined)}`;
      if (!prev[hitHash]) {
        prev[hitHash] = {
          count: 1,
          hit,
        };
      } else {
        prev[hitHash].count++;
      }

      return prev;
    }, {});

    sections.push({
      title: (
        <P variant="subtitle3" sx={{ marginY: 1, display: "block" }}>
          {groupHeader} ({lensHits.length})
        </P>
      ),
      items: Object.keys(hitMap).map((hitHash: string) => {
        const hitMeta = hitMap[hitHash];
        return (
          <LensHitSection
            key={hitMeta.hit.hitId}
            lensHit={hitMeta.hit}
            profileFieldId={profileFieldId}
            numHits={hitMeta.count}
          />
        );
      }),
      expanded: true,
    });
  }

  const labelsAndPageTypes: string[] = (pageType ? [displayPageType(pageType)] : []).concat(pageLabels);
  if (nonEmpty(labelsAndPageTypes)) {
    sections.push({
      title: (
        <P variant="subtitle3" sx={{ marginY: 1, display: "block" }}>
          Page Type
        </P>
      ),
      items: labelsAndPageTypes.map((pl) => (
        <P key={pl} variant="body4" sx={{ color: "grey.400", py: 1 }}>
          {pl}
        </P>
      )),
      expanded: true,
    });
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tag variant="profileField" tag={currentField?.name ?? "All"} sx={{ marginRight: 1 }} />
          <P sx={{ color: "grey.700" }}>Findings on Page {pageNum}</P>
        </Box>
        <CollapsibleSections sections={sections} />
      </CardContent>
    </Card>
  );
};

export default HitsOnPage;
